/**
 * Pick the section a reference points at.
 *
 * IT LISTS WHAT THE READER WILL SEE, numbered exactly as the document numbers them, because
 * choosing "4.2 Acceptance criteria" from a list is the only way somebody can be sure they picked
 * the right one. A list of raw ids would be unusable and a list of titles alone is ambiguous the
 * moment two sections are called "Scope".
 *
 * AND IT SAYS WHAT THE REFERENCE WILL READ AS. The same heading can be referred to by its number,
 * its title, or both, and the difference matters in a sentence -- "see 4.2" and "see Acceptance
 * criteria" are both ordinary English and the author is the one who knows which they meant.
 */
import React from "react";
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, Typography,
} from "@mui/material";

import { CHROME_FONT } from "./EditorToolbar";
import { OutlineEntry } from "./outline";
import { ReferenceShows } from "./CrossReference";
import { Grey3, Grey5, Grey17, Blue3 } from "../../theme/styles";

/** A heading, or a bookmark — the dialog treats them the same and only labels them apart. */
export type ReferenceTarget = OutlineEntry & { bookmark?: boolean };

export default function CrossReferenceDialog({ open, entries, onInsert, onClose }: {
    open: boolean;
    entries: ReferenceTarget[];
    onInsert: (index: number, shows: ReferenceShows, entry: ReferenceTarget) => void;
    onClose: () => void;
}) {
    const [chosen, setChosen] = React.useState<number | null>(null);
    const [shows, setShows] = React.useState<ReferenceShows>("number");

    React.useEffect(() => {
        if (open) { setChosen(entries.length ? 0 : null); setShows("number"); }
    }, [open, entries.length]);

    const preview = (entry: OutlineEntry) =>
        shows === "title" ? entry.text
        : shows === "both" ? (entry.number ? `${entry.number} ${entry.text}` : entry.text)
        : (entry.number || entry.text);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontSize: 17, fontFamily: CHROME_FONT }}>
                Refer to a section
            </DialogTitle>
            <DialogContent dividers sx={{ fontFamily: CHROME_FONT }}>
                {entries.length === 0 ? (
                    // NOT AN EMPTY LIST. A reference needs something to point at, and a heading is
                    // the thing it points at -- saying so is more use than a box with nothing in it.
                    <Typography sx={{ fontSize: 13.5, color: Grey5 }}>
                        This document has nothing to point at yet. A cross-reference needs a
                        heading, or a passage somebody has bookmarked.
                    </Typography>
                ) : (
                    <>
                        <Box sx={{ maxHeight: 280, overflowY: "auto", border: `1px solid ${Grey3}`,
                                   borderRadius: 1 }}>
                            {entries.map((entry, index) => (
                                <Box key={index}
                                     role="button" tabIndex={0}
                                     aria-label={`Refer to ${entry.text}`}
                                     onClick={() => setChosen(index)}
                                     sx={{ px: 3, py: 2, cursor: "pointer",
                                           borderBottom: "1px solid #F1F3F4",
                                           // INDENTED BY LEVEL, so the list reads as the document's
                                           // shape rather than as a flat set of names.
                                           pl: `${12 + (entry.level - 1) * 18}px`,
                                           backgroundColor: chosen === index ? "#E8F0FE" : "transparent",
                                           "&:hover": { backgroundColor: chosen === index
                                               ? "#E8F0FE" : "#F8F9FA" } }}>
                                    <Typography sx={{ fontSize: 13.5, color: Grey17,
                                                      fontFamily: CHROME_FONT }}>
                                        {entry.number ? `${entry.number}  ` : ""}{entry.text || "(untitled)"}
                                        {/* LABELLED, because "3.1 Torque figures" could be either a
                                            section or a passage inside one, and which it is decides
                                            what the reference will read as. */}
                                        {entry.bookmark && (
                                            <Typography component="span"
                                                        sx={{ fontSize: 11, color: Grey5, ml: 2,
                                                              fontFamily: CHROME_FONT }}>
                                                bookmark
                                            </Typography>
                                        )}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 3, mt: 4 }}>
                            <Typography sx={{ fontSize: 13, color: Grey17, fontFamily: CHROME_FONT }}>
                                Insert as
                            </Typography>
                            <Select size="small" value={shows}
                                    onChange={e => setShows(e.target.value as ReferenceShows)}
                                    sx={{ fontSize: 13, fontFamily: CHROME_FONT }}>
                                <MenuItem value="number" sx={{ fontSize: 13 }}>Number</MenuItem>
                                <MenuItem value="title" sx={{ fontSize: 13 }}>Title</MenuItem>
                                <MenuItem value="both" sx={{ fontSize: 13 }}>Number and title</MenuItem>
                            </Select>
                            {chosen !== null && entries[chosen] && (
                                <Typography sx={{ fontSize: 13, color: Blue3, fontFamily: CHROME_FONT }}>
                                    reads as “{preview(entries[chosen])}”
                                </Typography>
                            )}
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ fontFamily: CHROME_FONT }}>Cancel</Button>
                <Button variant="contained" disableElevation
                        disabled={chosen === null}
                        onClick={() => chosen !== null && onInsert(chosen, shows, entries[chosen])}
                        sx={{ fontFamily: CHROME_FONT }}>
                    Insert
                </Button>
            </DialogActions>
        </Dialog>
    );
}
